import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import { Page, Layout, Text, Box, InlineStack, Button, BlockStack, Toast } from '@shopify/polaris';
import { ImportIcon } from '@shopify/polaris-icons';
import { useDispatch, useSelector } from 'react-redux';
import { getProductAsync, importMetafieldsIntoProductAsync } from '../redux/product/index';
import { getAllProducts } from '../redux/product/slice';
import { getAllUserOnboarding } from '../redux/userOnboarding/slice';
import useOnboardingRedirect from '../redux/userOnBoardingRedirect';
import ProductTable from '../components/configure-products/ProductTable';
import Placeholder from '../components/common/Placeholder';
import ImportExportMetafields from '../components/configure-products/ImportExportMetafields';

const ConfigureProductsPage = () => {
    const dispatch = useDispatch();
    const { products, pageInfo, isProductLoading, totalProductsCount, isImportMetafieldsLoading } = useSelector(getAllProducts);
    const { userOnboardingData } = useSelector(getAllUserOnboarding);
    const [searchValue, setSearchValue] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [message, setMessage] = useState('');
    const [active, setActive] = useState(false);

    useOnboardingRedirect(userOnboardingData);

    const debouncedSearch = useMemo(
        () =>
            debounce(async (pagination) => {
                dispatch(getProductAsync({
                    payload: pagination
                }));
            }, 300),
        [dispatch]
    );

    const toggleActive = useCallback((message) => {
        setMessage(message);
        setActive((active) => !active);
    }, []);

    const toastMarkup = active ? (
        <Toast duration={2000} content={message} onDismiss={toggleActive} />
    ) : null;

    useEffect(() => {
        handlePaginationChange({
            after: '',
            before: '',
            query: searchValue
        });
    }, [searchValue]);

    const handleModalOpen = () => setIsModalOpen(!isModalOpen);

    const handlePaginationChange = (pagination) => {
        debouncedSearch(pagination);
    };

    const handleFiltersQueryChange = useCallback(
        (value) => setSearchValue(value),
        [],
    );

    const handleQueryValueRemove = useCallback(() => setSearchValue(''), []);

    const handleDrop = async ([file]) => {
        setFile(file);
    };

    const handleImportMetafields = () => {
        const formData = new FormData();
        formData.append('file', file);
        dispatch(importMetafieldsIntoProductAsync({
            payload: formData,
            callback: (success, message) => {
                if (success) {
                    setFile(null);
                    setIsModalOpen(false);
                    toggleActive(message);
                }
            }
        }));
    };

    return (
        <Page>
            <Layout>
                <Layout.Section>
                    <Box padding='200'>
                        <InlineStack align='space-between' blockAlign='center'>
                            <BlockStack gap='100'>
                                <Text variant='headingMd'>Configure Products</Text>
                                <Text variant='bodySm' tone='subdued'>
                                    Configure your products with metafields
                                </Text>
                            </BlockStack>
                            <Button
                                icon={ImportIcon}
                                variant='tertiary'
                                onClick={handleModalOpen}
                            >
                                Import
                            </Button>
                        </InlineStack>
                        <Placeholder />
                        <ProductTable
                            products={products}
                            pageInfo={pageInfo}
                            totalProductsCount={totalProductsCount}
                            onPaginationChange={handlePaginationChange}
                            isProductLoading={isProductLoading}
                            searchValue={searchValue}
                            handleFiltersQueryChange={handleFiltersQueryChange}
                            handleQueryValueRemove={handleQueryValueRemove}
                        />
                    </Box>
                </Layout.Section>
            </Layout>
            <ImportExportMetafields
                isModalOpen={isModalOpen}
                handleModalOpen={handleModalOpen}
                handleDrop={handleDrop}
                handleSubmit={handleImportMetafields}
                isLoading={isImportMetafieldsLoading}
                uploadedFile={file}
            />
            <Placeholder />
            {toastMarkup}
        </Page>
    );
};

export default ConfigureProductsPage;
